<template>
    <v-dialog
        v-model="dialog"
        persistent
        eager
        :max-width="dialogWidth"
        @keydown.esc="onEscapeKeydown"
    >
        <v-card role="region" aria-label="channel-lineup-bulk-assign">
            <v-card-title class="py-2">
                {{ dialogTitle }}
            </v-card-title>
            <v-card-text class="px-4 py-0">
                <v-container pa-0 fluid>
                    <v-row no-gutters>
                        <v-col>
                            <v-btn-toggle
                                v-model="visibilityFilter"
                                dense
                                mandatory
                                color="accent"
                                group
                            >
                                <v-btn value="PRIVATE">Private</v-btn>
                                <v-btn value="PUBLIC">Public</v-btn>
                            </v-btn-toggle>
                        </v-col>
                        <v-col>
                            <v-text-field
                                :value="queryFilter"
                                dense
                                clearable
                                label="Quick filter"
                                @input="onQueryFilterInput"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-container>
                <v-card elevation="10">
                    <v-card-text class="py-0 px-1">
                        <v-simple-table fixed-header height="200px">
                            <thead>
                                <tr tabindex="0">
                                    <th
                                        v-for="col in tableHeaders"
                                        :key="`col-${col.value}`"
                                    >
                                        {{ col.text }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="row in filteredItems"
                                    :key="`row-${row.id}`"
                                    :class="{
                                        'secondary--text text-subtitle-2':
                                            selectedItem &&
                                            selectedItem.id === row.id
                                    }"
                                    @click="onRowClick(row)"
                                >
                                    <td>
                                        {{
                                            row.displayName || row.locationName
                                        }}
                                    </td>
                                    <td>{{ row.visibility }}</td>
                                </tr>
                                <tr v-if="filteredItems.length === 0">
                                    <td
                                        colspan="10"
                                        class="warning--text text-center"
                                    >
                                        No data found...
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-card-text>
            <v-card-actions class="justify-end px-4">
                <v-btn small outlined class="mr-3" @click="onCancelClick"
                    >Cancel</v-btn
                >
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            small
                            color="primary"
                            :disabled="!allowAddButton"
                            v-on="on"
                            @click="onAddClick"
                            >Assign</v-btn
                        >
                    </template>
                    <span>Assing the selected channel map</span>
                </v-tooltip>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapState } from 'vuex';
import { isEmpty } from '@/common/helpers';
import { debounce } from 'debounce';

export default {
    name: 'LineupSelectionDialog',
    data() {
        return {
            dialog: false,
            resolve: null,
            reject: null,
            title: 'Select a Channel Lineup',
            options: {
                width: '500'
            },
            visibilityFilter: 'PRIVATE',
            queryFilter: '',
            tableHeaders: [
                { text: 'Name', value: 'locationName', sortable: true },
                { text: 'Visibility', value: 'visibility', sortable: true }
            ],
            selectedItem: null
        };
    },
    computed: {
        ...mapState('channelMaps', ['channelMaps']),
        filteredItems() {
            let result = [];
            // apply the visibility filter first
            result = this.channelMaps.filter(item => {
                if (!item.visibility) {
                    return true;
                }
                return item.visibility === this.visibilityFilter;
            });

            // apply the query filter
            if (!isEmpty(this.queryFilter)) {
                result = result.filter(item => {
                    const re = new RegExp(this.queryFilter.trimRight(), 'i');
                    if (item.displayName) {
                        return item.displayName.match(re);
                    }
                    return item.locationName.match(re);
                });
            }
            return result;
        },
        dialogTitle() {
            if (this.filteredItems.length) {
                return `${this.title} (${this.filteredItems.length})`;
            } else {
                return this.title;
            }
        },
        dialogWidth() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '90%';
            } else {
                return this.options.width;
            }
        },
        allowAddButton() {
            return !isEmpty(this.selectedItem);
        }
    },
    watch: {
        visibilityFilter() {
            this.selectedItem = null;
        },
        queryFilter() {
            this.selectedItem = null;
        }
    },
    methods: {
        // ================= Events =========================
        onQueryFilterInput: debounce(function(value) {
            this.queryFilter = value;
        }, 600),
        onRowClick(item) {
            if (item) {
                this.selectedItem = item;
            }
        },
        onCancelClick() {
            this.resolve(false);
            this.clearFilters();
            this.dialog = false;
        },
        onAddClick() {
            this.resolve(this.selectedItem);
            this.clearFilters();
            this.dialog = false;
        },
        onEscapeKeydown() {
            this.onCancelClick();
        },
        // ================= Others ============================
        open(title, options) {
            this.dialog = true;
            this.title = title;
            this.options = { ...this.options, ...options };
            return new Promise((resolve, reject) => {
                this.resolve = resolve;
                this.reject = reject;
            });
        },
        clearFilters() {
            this.visibilityFilter = 'PRIVATE';
            this.queryFilter = '';
            this.selectedItem = null;
        }
    }
};
</script>

<style lang="scss" scoped>
tbody tr {
    cursor: pointer;
}
</style>
